import React, { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { FieldWrapper } from './FieldWrapper';
import { CopySimple, Eye, EyeSlash } from '@phosphor-icons/react';
import { useNotificationStore } from '@/stores/notifications';

export const InputField = React.forwardRef((props, ref) => {
  const {
    type = 'text',
    label,
    className,
    registration,
    error,
    placeholder,
    defaultValue,
    name,
    onChange,
    value,
    disabled = false,
    labelTextClassName,
    errorClassName,
    showCopy = false,
    onKeyDown,
    onBlur,
    required = false,
  } = props;

  const [showPassword, setShowPassword] = useState(false);
  const { addNotification } = useNotificationStore();
  const inputRef = useRef(null);
  const copyIconRef = useRef(null);

  const handleMouseEnter = () => {
    if (showCopy) {
      copyIconRef.current.style.display = 'flex';
    }
  };

  const handleMouseLeave = () => {
    if (showCopy) {
      copyIconRef.current.style.display = 'none';
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      addNotification({
        type: 'success',
        title: 'Message copied to clipboard',
      });
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  useEffect(() => {
    const handleWheel = (e) => {
      if (type === 'number') {
        e.preventDefault();
      }
    };

    const inputElem = inputRef.current;
    inputElem.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      inputElem.removeEventListener('wheel', handleWheel);
    };
  }, [type]);

  return (
    <FieldWrapper
      label={label}
      error={error}
      className={labelTextClassName}
      errorClassName={errorClassName}
    >
      <div
        className="w-full relative"
        ref={inputRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <input
          onChange={onChange}
          disabled={disabled}
          name={name}
          type={showPassword ? 'text' : type}
          defaultValue={defaultValue}
          placeholder={placeholder}
          required={required}
          onKeyDown={onKeyDown}
          ref={ref}
          className={twMerge(
            'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500',
            className
          )}
          {...registration}
          value={value}
          onBlur={onBlur}
        />
        {type === 'password' && (
          <div
            className="absolute inset-y-0 right-0 text-gray-400 pr-3 flex items-center cursor-pointer"
            onClick={togglePasswordVisibility}
          >
            {showPassword ? <Eye size={20} /> : <EyeSlash size={20} />}
          </div>
        )}
        {showCopy && (
          <div
            ref={copyIconRef}
            className="absolute inset-y-0 right-0 text-gray-400 pr-3 items-center cursor-pointer hidden"
            onClick={handleCopy}
          >
            <CopySimple size={20} />
          </div>
        )}
      </div>
    </FieldWrapper>
  );
});
